import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorLike } from '@apollo/client';
import { Option } from '@lib/interfaces/form';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import Table from '@lib/components/Table/Table';
import getTableRowClassNameByValidityHint from '@lib/components/TaskCompletions/utils/getTableRowClassNameByValidityHint';
import { CompareCompletionColumn, CompareCompletionRow } from './utils/types';
import getCompareCompletionsTableColumns from './utils/getCompareCompletionsTableColumns';
import styles from './TaskCompletionsCompareTable.module.scss';

type Props = {
  columns: CompareCompletionColumn[];
  rows: CompareCompletionRow[];
  firstLoading: boolean;
  error?: ErrorLike;
  statusFilter: Option[];
  isViewLocked?: boolean;
  isFullAdmin?: boolean;
  onUpgradePlan?: () => void;
  onDocumentClick?: (documentationId: string) => void;
};

export default function TaskCompletionsCompareTable({
  columns,
  rows: allRows,
  firstLoading,
  error,
  statusFilter,
  isViewLocked,
  isFullAdmin,
  onUpgradePlan,
  onDocumentClick,
}: Props) {
  const { t } = useTranslation();

  // Compare mode compares a fixed set of completions (the columns) — there's
  // no single status to show per row here (each column has its own answer,
  // rendered with its own status in its response cell), so the filter keeps
  // a question row when *any* of the compared completions' answers matches
  // one of the selected statuses, rather than dropping whole completions.
  const rows = useMemo(() => {
    if (!statusFilter.length) return allRows;
    const allowedHints = new Set(
      statusFilter.map(({ value }) => String(value)),
    );
    return allRows.filter((row) =>
      row.columnItems.some(
        (columnItem) =>
          columnItem?.answer?.validityHint &&
          allowedHints.has(columnItem.answer.validityHint),
      ),
    );
  }, [allRows, statusFilter]);

  const tableColumns = useMemo(
    () =>
      getCompareCompletionsTableColumns({
        columns,
        responseArgs: {
          t,
          onDocumentClick,
          isViewLocked,
          isFullAdmin,
          onUpgradePlan,
        },
      }),
    [columns, t, onDocumentClick, isViewLocked, isFullAdmin, onUpgradePlan],
  );

  let content: React.ReactNode;
  if (error) {
    content = (
      <ErrorPage description={getDescriptionFromError(error)} status={500} />
    );
  } else if (firstLoading) {
    content = <CircleLoader />;
  } else {
    content = (
      <Table<CompareCompletionRow>
        id="daily-inspection-compare-completions"
        columns={tableColumns}
        data={rows}
        totalEntries={rows.length}
        getRowClassName={(item) =>
          getTableRowClassNameByValidityHint(item.worstValidityHint)
        }
      />
    );
  }

  return <div className={styles.tableWrapper}>{content}</div>;
}
